import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import {
  readFlowRate,
  readSchedule,
  readStopTimerPayload,
  scheduleRequestBody,
  toDeviceDetails,
  type DeviceDetails,
  type DeviceSchedule,
  type FlowRateRange,
  type FlowRateSeries,
  type StopTimer,
} from './details-schemas';

/** Full detail payload for one device. 404 → the page renders its not-found state. */
export async function fetchDeviceDetails(deviceId: string): Promise<DeviceDetails> {
  const payload = await apiClient.get<unknown>(endpoints.devices.detail(deviceId));
  return toDeviceDetails(payload);
}

/**
 * The flow-rate history behind the chart, for one range. `range` is required despite the spec
 * documenting a default. Aggregation is server-side per range, so a range change refetches rather
 * than re-slicing one dataset.
 */
export async function fetchFlowRate(input: {
  deviceId: string;
  range: FlowRateRange;
}): Promise<FlowRateSeries> {
  const payload = await apiClient.get<unknown>(endpoints.devices.flowRate(input.deviceId), {
    params: { range: input.range },
  });
  return readFlowRate(payload);
}

/**
 * The device's schedule, or `null` when it has none — the endpoint answers **200 with
 * `data: null`** in that case, not a 404, so this must not be treated as a failure.
 */
export async function fetchSchedule(deviceId: string): Promise<DeviceSchedule | null> {
  const payload = await apiClient.get<unknown>(endpoints.devices.schedule(deviceId));
  return readSchedule(payload);
}

/**
 * Create or replace the schedule. Returns the server's stored version (with its `id`), which is
 * what the drawer should reopen against; falls back to what was sent when the response body is
 * empty, since a 200 means it was accepted either way.
 */
export async function saveSchedule(input: {
  deviceId: string;
  schedule: DeviceSchedule;
}): Promise<DeviceSchedule> {
  const payload = await apiClient.put<unknown>(
    endpoints.devices.schedule(input.deviceId),
    scheduleRequestBody(input.schedule),
  );
  return readSchedule(payload) ?? input.schedule;
}

/** Remove the schedule. Idempotent from the UI's point of view — 200 even with none set. */
export async function deleteSchedule(deviceId: string): Promise<void> {
  await apiClient.delete(endpoints.devices.schedule(deviceId));
}

/**
 * The currently running stop timer. An inactive {@link StopTimer} means there is none — the
 * endpoint's own answer, not a failure.
 */
export async function fetchStopTimer(deviceId: string): Promise<StopTimer> {
  const payload = await apiClient.get<unknown>(endpoints.devices.stopTimer(deviceId));
  return readStopTimerPayload(payload);
}

/**
 * Start a stop timer. `durationMinutes` is clamped to the endpoint's documented 1–1440 range,
 * which is also the drawer's own picker bounds.
 *
 * A 201 means the timer was accepted, so when the response echoes nothing back the timer is
 * synthesised from what was sent — the countdown has to start either way.
 */
export async function startStopTimer(input: {
  deviceId: string;
  durationMinutes: number;
}): Promise<StopTimer> {
  const durationMinutes = Math.min(1440, Math.max(1, Math.round(input.durationMinutes)));
  const payload = await apiClient.post<unknown>(endpoints.devices.stopTimer(input.deviceId), {
    durationMinutes,
  });

  const parsed = readStopTimerPayload(payload);
  if (parsed.active || parsed.stopsAt) return parsed;

  return {
    enabled: true,
    active: true,
    stopsAt: new Date(Date.now() + durationMinutes * 60_000).toISOString(),
    startedAt: new Date().toISOString(),
    durationMinutes,
  };
}
